import { API } from "@swivl-mono/api-helper";
import { FMSInterface, GroupInterface } from "@swivl-mono/types";
import { Button, Spinner } from "@swivl-mono/ui";
import { useEffect, useState } from "react";

import { Member } from "../../../Models/Member/Member.Current";
import { AlertsModel } from "../../../Models/Notifications/Alerts.Model";
import { styles } from "./shared/onboarding.styles";
import { extractSheetId, getColumnLetter } from "./shared/onboarding.utils";

interface GoogleSheetData {
  headers: string[];
  records: Record<string, string>[];
}

export const loadGoogleSheetNames = async (
  sheetId: string,
  setIsLoadingSheets: (loading: boolean) => void,
  setAvailableSheets: (sheets: string[]) => void,
  setSelectedSheet: (sheet: string) => void
) => {
  setIsLoadingSheets(true);
  setAvailableSheets([]);
  setSelectedSheet("");

  try {
    const sheetNames = await API.Google.getGoogleSheetNames(sheetId);
    setAvailableSheets(sheetNames);

    if (sheetNames.length === 1) {
      setSelectedSheet(sheetNames[0]);
    }
  } catch (error) {
    console.error("Error loading sheet names:", error);
    AlertsModel.errorToast(`Failed to load sheet names: ${error instanceof Error ? error.message : "Unknown error"}`);
  } finally {
    setIsLoadingSheets(false);
  }
};

export function GoogleSheetImportView() {
  const [sheetUrl, setSheetUrl] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [fmsIntegrations, setFmsIntegrations] = useState<FMSInterface[]>([]);
  const [isLoadingFMS, setIsLoadingFMS] = useState(true);
  const [groups, setGroups] = useState<GroupInterface[]>([]);
  const [isLoadingGroups, setIsLoadingGroups] = useState(true);
  const [selectedGroupId, setSelectedGroupId] = useState<number | null>(null);
  const [availableSheets, setAvailableSheets] = useState<string[]>([]);
  const [selectedSheet, setSelectedSheet] = useState<string>("");
  const [isLoadingSheets, setIsLoadingSheets] = useState(false);

  useEffect(() => {
    loadFMSIntegrations();
    loadGroups();
  }, []);

  const loadFMSIntegrations = async () => {
    try {
      const orgId = Member.orgId;
      if (!orgId) {
        AlertsModel.errorToast("No org selected. Please login to a valid account.");
        setIsLoadingFMS(false);
        return;
      }

      const integrations = await API.FMS.getFMSByOrgId(orgId);
      setFmsIntegrations(integrations || []);

      if (!integrations || integrations.length === 0) {
        AlertsModel.errorToast("No FMS integrations found. Please set up an FMS integration, then refresh this page.");
      }
    } catch (error) {
      console.error("Error loading FMS integrations:", error);
      AlertsModel.errorToast("Failed to load FMS integrations");
    } finally {
      setIsLoadingFMS(false);
    }
  };

  const loadGroups = async () => {
    try {
      const orgId = Member.orgId;
      if (!orgId) {
        setIsLoadingGroups(false);
        return;
      }

      const groupsAndLocations = await API.Locations.getGroupsAndLocations(orgId);
      setGroups(groupsAndLocations || []);

      if (groupsAndLocations && groupsAndLocations.length === 1) {
        setSelectedGroupId(groupsAndLocations[0].id);
      }
    } catch (error) {
      console.error("Error loading groups:", error);
      AlertsModel.errorToast("Failed to load groups");
    } finally {
      setIsLoadingGroups(false);
    }
  };

  const loadSheetNames = async (sheetId: string) => {
    return loadGoogleSheetNames(sheetId, setIsLoadingSheets, setAvailableSheets, setSelectedSheet);
  };

  const handleUrlChange = (url: string) => {
    setSheetUrl(url);
    setValidationErrors([]); // Clear both errors and success

    if (url.trim()) {
      try {
        const sheetId = extractSheetId(url);
        loadSheetNames(sheetId);
      } catch (error) {
        setAvailableSheets([]);
        setSelectedSheet("");
      }
    } else {
      setAvailableSheets([]);
      setSelectedSheet("");
    }
  };

  const readFromGoogleSheet = async (sheetId: string, sheetName?: string): Promise<GoogleSheetData> => {
    const range = sheetName ? `${sheetName}!A:ZZ` : "A:ZZ";
    const response = await API.Google.readGoogleSheet(sheetId, range);
    return {
      headers: response.headers,
      records: response.records
    };
  };

  const validateSheetData = (data: GoogleSheetData): string[] => {
    const errors: string[] = [];

    if (groups.length === 0) {
      errors.push("No groups found. At least one group is required for location assignment.");
    } else if (groups.length > 1 && !selectedGroupId) {
      errors.push("Please select a group for the new locations.");
    }

    const seen = new Set<string>();
    data.records.forEach((record, index) => {
      const name = record["Location Name"]?.trim()?.toLowerCase();
      if (name && seen.has(name)) {
        errors.push(`Row ${index + 2}: Duplicate location name "${record["Location Name"]}"`);
      }
      if (name) seen.add(name);
    });

    // Only check for existing location names if the row doesn't have a Location ID
    // (rows with Location IDs are updates, not new creations)
    const existingNames = groups.flatMap((g) => g.locations?.map((l) => l.name.toLowerCase()) || []);
    data.records.forEach((record, index) => {
      const existingLocationId = record["Location ID"]?.trim();
      const name = record["Location Name"]?.trim()?.toLowerCase();

      // Skip this check if the row has a Location ID (it's an update)
      if (!existingLocationId && name && existingNames.includes(name)) {
        errors.push(`Row ${index + 2}: Location "${record["Location Name"]}" already exists`);
      }
    });

    if (fmsIntegrations.length === 0) {
      errors.push("No FMS integrations found. Please set up an FMS integration first.");
    } else if (fmsIntegrations.length > 1) {
      const hasFmsColumn = data.headers.includes("FMS Integration ID") || data.headers.includes("FMS Integration");
      if (!hasFmsColumn) {
        const integrationList = fmsIntegrations.map((fms) => `  • ${fms.name} (ID: ${fms.id})`).join("\n");
        errors.push(
          `Multiple FMS integrations found. Add an 'FMS Integration ID' column to the Google Sheet with the numeric ID for each location.\n\n` +
            `To find the ID: go to https://v3.swivl.studio/settings/fms-integrations, open the integration, and use the number from the URL (e.g., 656 from https://v3.swivl.studio/fms/656).\n\n` +
            `Available integrations:\n${integrationList}`
        );
      } else {
        data.records.forEach((record, index) => {
          const rowNum = index + 2;
          const fmsIntegrationId = (record["FMS Integration ID"] ?? record["FMS Integration"])?.trim();

          if (!fmsIntegrationId) {
            errors.push(`Row ${rowNum}: FMS Integration is required when multiple integrations exist`);
          } else {
            const validId = fmsIntegrations.find((fms) => fms.id.toString() === fmsIntegrationId);
            if (!validId) {
              const validIds = fmsIntegrations.map((fms) => fms.id).join(", ");
              errors.push(`Row ${rowNum}: Invalid FMS Integration '${fmsIntegrationId}'. Valid IDs: ${validIds}`);
            }
          }
        });
      }
    }

    const requiredColumns = ["Location Name"];
    const missingColumns = requiredColumns.filter((col) => !data.headers.includes(col));

    if (missingColumns.length > 0) {
      errors.push(`Missing required columns: ${missingColumns.join(", ")}`);
    }

    if (data.records.length === 0) {
      errors.push("No data rows found in the sheet");
    }

    data.records.forEach((record, index) => {
      const rowNum = index + 2; // +2 because row 1 is headers, and we want 1-based indexing

      if (!record["Location Name"]?.trim()) {
        errors.push(`Row ${rowNum}: Location Name is required`);
      }
    });

    return errors;
  };

  const createLocationsFromSheet = async (data: GoogleSheetData, sheetId: string, sheetName?: string) => {
    const orgId = Member.orgId;
    if (!orgId) throw new Error("No org selected");

    const targetGroupId = selectedGroupId || groups[0]?.id;
    if (!targetGroupId) throw new Error("No group selected");

    const results = {
      successful: 0,
      failed: 0,
      updated: 0,
      errors: [] as string[],
      locationUpdates: [] as { rowNum: number; locationId: number }[]
    };

    const hasLocationIdColumn = data.headers.includes("Location ID");
    let locationIdColumnLetter: string | null = null;

    if (hasLocationIdColumn) {
      const columnIndex = data.headers.indexOf("Location ID");
      locationIdColumnLetter = getColumnLetter(columnIndex);
    }

    // FMSSyncEnabled and FMSIntegrationId inherit, so set once at org or group level
    const inheritanceGroupId = groups.length > 1 ? targetGroupId : undefined;
    await API.LocationVars.createVariable("FMSSyncEnabled", "1", orgId, inheritanceGroupId);
    if (fmsIntegrations.length === 1) {
      await API.LocationVars.createVariable("FMSIntegrationId", fmsIntegrations[0].id.toString(), orgId, inheritanceGroupId);
    }

    for (let i = 0; i < data.records.length; i++) {
      const record = data.records[i];
      const rowNum = i + 2;
      const locationName = record["Location Name"]?.trim();
      const fmsId = record["FMS ID"]?.trim();
      const existingLocationId = record["Location ID"]?.trim();

      if (!locationName) continue;

      try {
        let locationId: number;

        // Check if location already exists (has Location ID)
        if (existingLocationId && !isNaN(parseInt(existingLocationId))) {
          // Location exists - update its variables
          locationId = parseInt(existingLocationId);

          await API.LocationVars.createVariable("Name", locationName, orgId, undefined, locationId);
          await API.LocationVars.createVariable("FMSId", fmsId, orgId, undefined, locationId);

          if (fmsIntegrations.length > 1) {
            const fmsIntegrationId = (record["FMS Integration ID"] ?? record["FMS Integration"])?.trim();
            if (fmsIntegrationId) {
              await API.LocationVars.createVariable("FMSIntegrationId", fmsIntegrationId, orgId, undefined, locationId);
            }
          }

          results.updated++;
        } else {
          // Location doesn't exist - create it
          const newLocation = await API.Locations.createLocation(orgId, locationName, targetGroupId);
          locationId = newLocation.id;

          await API.LocationVars.createVariable("Name", locationName, orgId, undefined, locationId);
          await API.LocationVars.createVariable("FMSId", fmsId, orgId, undefined, locationId);

          if (fmsIntegrations.length > 1) {
            const fmsIntegrationId = (record["FMS Integration ID"] ?? record["FMS Integration"])?.trim();
            if (fmsIntegrationId) {
              await API.LocationVars.createVariable("FMSIntegrationId", fmsIntegrationId, orgId, undefined, locationId);
            }
          }

          results.successful++;

          if (hasLocationIdColumn) {
            results.locationUpdates.push({ rowNum, locationId });
          }
        }
      } catch (error) {
        console.error(`Error processing location "${locationName}":`, error);
        results.failed++;
        results.errors.push(`Row ${rowNum} (${locationName}): ${error instanceof Error ? error.message : "Unknown error"}`);
      }
    }

    if (hasLocationIdColumn && results.locationUpdates.length > 0 && locationIdColumnLetter) {
      try {
        const updateValues = results.locationUpdates.map((update) => [update.locationId.toString()]);
        const sheetPrefix = sheetName ? `${sheetName}!` : "";
        const startRow = results.locationUpdates[0].rowNum;
        const endRow = results.locationUpdates[results.locationUpdates.length - 1].rowNum;
        const updateRange = `${sheetPrefix}${locationIdColumnLetter}${startRow}:${locationIdColumnLetter}${endRow}`;

        await API.Google.updateGoogleSheet(sheetId, updateRange, updateValues);
      } catch (updateError) {
        console.error("Error updating Google Sheet with Location IDs:", updateError);
        results.errors.push(
          `Note: Location IDs could not be written back to Google Sheet: ${updateError instanceof Error ? updateError.message : "Unknown error"}`
        );
      }
    }

    return results;
  };

  const handleProcessSheet = async () => {
    if (!sheetUrl.trim()) {
      AlertsModel.errorToast("Please enter a Google Sheet URL");
      return;
    }

    setIsProcessing(true);
    setValidationErrors([]);

    try {
      const sheetId = extractSheetId(sheetUrl);

      const sheetData = await readFromGoogleSheet(sheetId, selectedSheet || undefined);

      const errors = validateSheetData(sheetData);

      if (errors.length > 0) {
        setValidationErrors(errors);
        AlertsModel.errorToast(`Sheet validation failed. Found ${errors.length} error(s).`);
        return;
      }

      const results = await createLocationsFromSheet(sheetData, sheetId, selectedSheet || undefined);

      if (results.successful > 0 || results.updated > 0) {
        const parts: string[] = [];
        if (results.successful > 0) parts.push(`created ${results.successful}`);
        if (results.updated > 0) parts.push(`updated ${results.updated}`);
        const message = `Successfully ${parts.join(", ")} location(s)${results.failed > 0 ? ` (${results.failed} failed)` : ""}`;
        AlertsModel.successToast(message);

        if (results.failed === 0) {
          setValidationErrors([`SUCCESS:${results.successful}:${results.updated}`]); // Special marker for success
        } else {
          setValidationErrors(results.errors);
        }
      } else {
        setValidationErrors(results.errors);
      }
    } catch (error) {
      console.error("Error processing sheet:", error);
      const errorMessage = error instanceof Error ? error.message : "Unknown error";
      AlertsModel.errorToast(`Failed to process sheet: ${errorMessage}`);
      setValidationErrors([errorMessage]);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div style={styles.container}>
      <h2 style={styles.heading}>3️⃣ Create Locations & Activate Sync</h2>
      <p style={styles.description}>
        Create locations and enable FMS sync from the <strong>Sheet1</strong> tab of the Google Sheet. This does not import all info from the sheet -
        after refreshing, locations will still look bare bones. Internal swivl <strong>Location IDs</strong> will be written back to the sheet
        automatically.
      </p>

      <div style={styles.requirementsBox}>
        <h4 style={styles.heading}>Requirements:</h4>
        <ol style={styles.description}>
          <li>❓ Are you logged into the correct org?</li>
          <li>
            {isLoadingFMS ? (
              <>⏳ Checking FMS integrations...</>
            ) : fmsIntegrations.length > 0 ? (
              <>
                ✅ FMS integration found:{" "}
                {fmsIntegrations.length === 1 ? fmsIntegrations[0].name : `${fmsIntegrations.length} integrations available`}
              </>
            ) : (
              <>
                ❌ An{" "}
                <a href="/settings/fms-integrations" target="_blank" rel="noopener noreferrer" style={{ color: "#0066cc" }}>
                  FMS integration
                </a>{" "}
                needs to be setup
              </>
            )}
          </li>
          <li>
            {isLoadingGroups ? (
              <>⏳ Checking groups...</>
            ) : groups.length > 0 ? (
              <>✅ Group found: {groups.length === 1 ? groups[0].name : `${groups.length} groups available`}</>
            ) : (
              <>
                ❌ At least one{" "}
                <a href="/properties" target="_blank" rel="noopener noreferrer" style={{ color: "#0066cc" }}>
                  group
                </a>{" "}
                is required
              </>
            )}
          </li>
          <li>📝 The 'Location Name' column of the Google Sheet is filled out</li>
        </ol>
      </div>

      {/* Group Selection - only show when multiple groups */}
      {groups.length > 1 && (
        <div style={styles.inputWrapper}>
          <select
            id="groupSelect"
            value={selectedGroupId || ""}
            onChange={(e) => setSelectedGroupId(e.target.value ? parseInt(e.target.value) : null)}
            disabled={isProcessing}
            style={styles.input}
          >
            <option value="">Please select a group...</option>
            {groups.map((group) => (
              <option key={group.id} value={group.id}>
                {group.name}
              </option>
            ))}
          </select>
        </div>
      )}

      <div style={styles.inputWrapper}>
        <input
          id="sheetUrl"
          type="url"
          value={sheetUrl}
          onChange={(e) => handleUrlChange(e.target.value)}
          placeholder="Google Sheet URL: https://docs.google.com/spreadsheets/d/..."
          disabled={isProcessing || fmsIntegrations.length === 0}
          style={styles.input}
        />
      </div>

      {/* Sheet Selection - only show when multiple sheets available */}
      {availableSheets.length > 1 && (
        <div style={styles.inputWrapper}>
          <label htmlFor="sheetSelect" style={{ display: "block", marginBottom: "6px", fontSize: "14px", fontWeight: 500, color: "#374151" }}>
            Sheet:
          </label>

          <select
            id="sheetSelect"
            value={selectedSheet}
            onChange={(e) => setSelectedSheet(e.target.value)}
            disabled={isProcessing || isLoadingSheets}
            style={styles.input}
          >
            <option value="">Please select a sheet...</option>
            {availableSheets.map((sheetName) => (
              <option key={sheetName} value={sheetName}>
                {sheetName}
              </option>
            ))}
          </select>
        </div>
      )}

      {isLoadingSheets && <div style={styles.loadingText}>⏳ Loading sheet names...</div>}

      <div style={styles.inputWrapper}>
        <Button
          onClick={handleProcessSheet}
          disabled={
            isProcessing ||
            !sheetUrl.trim() ||
            fmsIntegrations.length === 0 ||
            groups.length === 0 ||
            (groups.length > 1 && !selectedGroupId) ||
            (availableSheets.length > 1 && !selectedSheet) ||
            isLoadingSheets
          }
        >
          {isProcessing ? (
            <>
              <Spinner />
              {" Creating Locations..."}
            </>
          ) : (
            "Create Locations"
          )}
        </Button>
      </div>

      {/* Results Display */}
      {validationErrors.length > 0 &&
        (validationErrors[0]?.startsWith("SUCCESS:") ? (
          (() => {
            const parts = validationErrors[0].split(":");
            const created = parseInt(parts[1]);
            const updated = parseInt(parts[2]);
            const summaryParts: string[] = [];
            if (created > 0) summaryParts.push(`Created ${created}`);
            if (updated > 0) summaryParts.push(`Updated ${updated}`);

            return (
              <div style={styles.successBox}>
                <h3 style={styles.successHeading}>Locations Processed Successfully</h3>
                <div style={{ fontSize: "14px", color: "#374151" }}>
                  <p>✅ {summaryParts.join(", ")} location(s) and activated sync</p>
                  <p>➡️ Fewer than ~15 locations? Skip to step 5</p>
                  <p>🕐 More than ~15 locations? Wait ~2 hours for FMS sync to run, then proceed to step 4</p>
                </div>
              </div>
            );
          })()
        ) : (
          <div style={styles.errorBox}>
            <h3 style={styles.errorHeading}>Processing Errors</h3>
            <div style={{ fontSize: "14px", color: "#374151" }}>
              {validationErrors.map((error, index) => (
                <p key={index} style={{ margin: "4px 0" }}>
                  ❌ {error}
                </p>
              ))}
            </div>
          </div>
        ))}
    </div>
  );
}
